import { t } from 'i18next';
import { useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { MainButton } from '../../components/Buttons/MainButton';
import CircleLoadingIndicator from '../../components/LoadingIndicators/CircleLoadingIndicator';
import ProfileImage from '../../components/ProfileImage';
import { compressAndResizeImage } from '../../routes/CreateUpdateAssetPage/Fields/ImageManagementSelection/utils';
import { borderRadius } from '../../theme/borderRadius';
import { colors, hexToRgbA } from '../../theme/colors';
import spacing from '../../theme/spacing';
import { Dialog } from '@mui/material';
import { modalPaperRootBackdropStyles } from '../../components/Modals/utils';

interface ProfileImageManagementProps {
  currentImageUrl?: string | null;
  username?: string;
  onImageChange: (file: File) => Promise<void>;
  size?: number;
}

const ProfileImageManagement = ({
  currentImageUrl,
  username,
  onImageChange,
  size = 100,
}: ProfileImageManagementProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result as string);
      setIsModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createCroppedFile = async (
    imageSrc: string,
    pixelCrop: Area,
  ): Promise<File> => {
    const image = new Image();
    image.src = imageSrc;

    return new Promise((resolve) => {
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) throw new Error('No 2d context');

        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height,
        );

        canvas.toBlob((blob) => {
          if (!blob) throw new Error('Canvas to Blob failed');
          const file = new File([blob], 'profile-image.jpg', {
            type: 'image/jpeg',
          });
          resolve(file);
        }, 'image/jpeg');
      };
    });
  };

  const handleCropSave = async () => {
    if (!cropImage || !croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      const croppedFile = await createCroppedFile(cropImage, croppedAreaPixels);
      const compressedImage = await compressAndResizeImage(croppedFile);
      await onImageChange(compressedImage.file);
      setIsModalOpen(false);
      setCropImage(null);
    } catch (error) {
      console.error('Error processing image:', error);
    }
    setIsProcessing(false);
  };

  return (
    <>
      <div
        style={{
          position: 'relative',
          width: size,
          height: size,
        }}
      >
        <ProfileImage
          src={currentImageUrl}
          username={username}
          size={size}
          style={{
            borderRadius: `${borderRadius.large}px`,
          }}
        />
        <input
          ref={inputFileRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <MainButton
          onPress={() => inputFileRef.current?.click()}
          variant="outlined"
          size="small"
          typeStyle="secondary"
          text={t('common.change')}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            width: size,
            border: 'none',
            borderRadius: borderRadius.large,
            background: hexToRgbA(colors.backgroundMediumLight, 0.8),
          }}
        />
      </div>

      {isModalOpen && (
        <Dialog
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setCropImage(null);
          }}
          PaperProps={{
            style: {
              ...modalPaperRootBackdropStyles,
              background: colors.backgroundMediumLight,
              borderRadius: borderRadius.large,
              padding: spacing.large,
              width: '90%',
              maxWidth: 400,
              margin: 0,
              overflow: 'hidden',
            },
          }}
          sx={{
            '& .MuiDialog-container': {
              justifyContent: 'center',
              alignItems: 'center',
            },
            '& .MuiDialog-paper': {
              boxSizing: 'border-box',
            },
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: size * 2,
                maxWidth: '100%',
                aspectRatio: '1 / 1',
                borderRadius: borderRadius.large,
                overflow: 'hidden',
                background: colors.backgroundMediumLight,
              }}
            >
              {cropImage && (
                <Cropper
                  image={cropImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                />
              )}
            </div>

            <div
              style={{
                display: 'flex',
                gap: spacing.medium,
                marginTop: spacing.large,
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <MainButton
                variant="outlined"
                onPress={() => {
                  setIsModalOpen(false);
                  setCropImage(null);
                }}
                typeStyle="secondary"
                fullWidth
                style={{
                  flex: 1,
                }}
                text={t('common.cancel')}
              />
              <MainButton
                onPress={handleCropSave}
                style={{ flex: 1 }}
                disabled={isProcessing}
                fullWidth
              >
                <>
                  {isProcessing ? (
                    <CircleLoadingIndicator size={24} />
                  ) : (
                    t('common.save')
                  )}
                </>
              </MainButton>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default ProfileImageManagement;
